import { CheckboxValue } from "../checkbox/redo-checkbox";

export interface RedoCheckboxTreeItem {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface RedoCheckboxTreeGroup {
  id: string;
  label: string;
  itemIds: readonly string[];
  /** Consecutive groups that share a section render one heading. */
  section?: string;
}

export type RedoCheckboxTreeNode =
  | {
      kind: "group";
      nodeId: string;
      group: RedoCheckboxTreeGroup;
      items: readonly RedoCheckboxTreeItem[];
      expanded: boolean;
    }
  | {
      kind: "item";
      nodeId: string;
      groupId: string;
      item: RedoCheckboxTreeItem;
    };

interface VisibleCheckboxTreeGroup {
  group: RedoCheckboxTreeGroup;
  items: RedoCheckboxTreeItem[];
}

export function groupNodeId(groupId: string): string {
  return `group:${groupId}`;
}

export function itemNodeId(groupId: string, itemId: string): string {
  return `item:${groupId}:${itemId}`;
}

export function itemsByIdMap(
  items: readonly RedoCheckboxTreeItem[],
): Map<string, RedoCheckboxTreeItem> {
  return new Map(items.map((item) => [item.id, item]));
}

function isItemDisabled(
  itemId: string,
  itemsById?: ReadonlyMap<string, RedoCheckboxTreeItem>,
  disabledIds?: ReadonlySet<string>,
): boolean {
  if (disabledIds?.has(itemId)) {
    return true;
  }
  return itemsById?.get(itemId)?.disabled === true;
}

function enabledItemIds(
  itemIds: readonly string[],
  itemsById?: ReadonlyMap<string, RedoCheckboxTreeItem>,
  disabledIds?: ReadonlySet<string>,
): string[] {
  return itemIds.filter(
    (itemId) => !isItemDisabled(itemId, itemsById, disabledIds),
  );
}

export function getGroupCheckboxValue(
  itemIds: readonly string[],
  selected: ReadonlySet<string>,
  itemsById?: ReadonlyMap<string, RedoCheckboxTreeItem>,
  disabledIds?: ReadonlySet<string>,
): CheckboxValue {
  const ids = enabledItemIds(itemIds, itemsById, disabledIds);
  if (ids.length === 0) {
    return false;
  }
  let selectedCount = 0;
  for (const id of ids) {
    if (selected.has(id)) {
      selectedCount++;
    }
  }
  if (selectedCount === 0) {
    return false;
  }
  if (selectedCount === ids.length) {
    return true;
  }
  return "indeterminate";
}

export function withItemIdsSelection(
  selected: ReadonlySet<string>,
  itemIds: readonly string[],
  checked: boolean,
  itemsById?: ReadonlyMap<string, RedoCheckboxTreeItem>,
  disabledIds?: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selected);
  for (const id of enabledItemIds(itemIds, itemsById, disabledIds)) {
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
  }
  return next;
}

export function withItemSelection(
  selected: ReadonlySet<string>,
  itemId: string,
  checked: boolean,
): Set<string> {
  const next = new Set(selected);
  if (checked) {
    next.add(itemId);
  } else {
    next.delete(itemId);
  }
  return next;
}

export function countSelectedInGroup(
  itemIds: readonly string[],
  selected: ReadonlySet<string>,
): number {
  return itemIds.filter((id) => selected.has(id)).length;
}

function itemMatchesQuery(item: RedoCheckboxTreeItem, query: string): boolean {
  return (
    item.label.toLowerCase().includes(query) ||
    item.id.toLowerCase().includes(query)
  );
}

export function filterCheckboxTree(
  items: readonly RedoCheckboxTreeItem[],
  groups: readonly RedoCheckboxTreeGroup[],
  searchString: string,
): { visibleGroups: VisibleCheckboxTreeGroup[]; visibleItemIds: string[] } {
  const itemsById = itemsByIdMap(items);
  const query = searchString.trim().toLowerCase();
  const visibleGroups: VisibleCheckboxTreeGroup[] = [];
  const visibleItemIds: string[] = [];
  const seenItemIds = new Set<string>();

  for (const group of groups) {
    const groupItems: RedoCheckboxTreeItem[] = [];
    for (const itemId of group.itemIds) {
      const item = itemsById.get(itemId);
      if (item) {
        groupItems.push(item);
      }
    }

    const groupLabelMatches =
      query.length > 0 && group.label.toLowerCase().includes(query);
    const matchingItems =
      query.length === 0 || groupLabelMatches
        ? groupItems
        : groupItems.filter((item) => itemMatchesQuery(item, query));

    if (matchingItems.length === 0) {
      continue;
    }

    visibleGroups.push({ group, items: matchingItems });
    for (const item of matchingItems) {
      if (!seenItemIds.has(item.id)) {
        seenItemIds.add(item.id);
        visibleItemIds.push(item.id);
      }
    }
  }

  return { visibleGroups, visibleItemIds };
}

export function flattenVisibleTreeNodes(
  visibleGroups: readonly VisibleCheckboxTreeGroup[],
  expandedGroupIds: ReadonlySet<string>,
  forceExpand: boolean,
): RedoCheckboxTreeNode[] {
  const nodes: RedoCheckboxTreeNode[] = [];

  for (const { group, items } of visibleGroups) {
    const expanded = forceExpand || expandedGroupIds.has(group.id);
    nodes.push({
      kind: "group",
      nodeId: groupNodeId(group.id),
      group,
      items,
      expanded,
    });
    if (!expanded) {
      continue;
    }
    for (const item of items) {
      nodes.push({
        kind: "item",
        nodeId: itemNodeId(group.id, item.id),
        groupId: group.id,
        item,
      });
    }
  }

  return nodes;
}

export function toggleExpandedGroupIds(
  expandedGroupIds: ReadonlySet<string>,
  groupId: string,
  expanded?: boolean,
): Set<string> {
  const next = new Set(expandedGroupIds);
  const shouldExpand = expanded ?? !next.has(groupId);
  if (shouldExpand) {
    next.add(groupId);
  } else {
    next.delete(groupId);
  }
  return next;
}

export function expandAllGroupIds(
  groups: readonly { group: { id: string } }[],
): Set<string> {
  return new Set(groups.map(({ group }) => group.id));
}

export function findTypeaheadIndex(
  labels: readonly string[],
  query: string,
  startIndex: number,
): number | undefined {
  if (query.length === 0 || labels.length === 0) {
    return undefined;
  }
  const needle = query.toLowerCase();
  const from = startIndex + 1;
  for (let offset = 0; offset < labels.length; offset++) {
    const index = (from + offset) % labels.length;
    const label = labels[index];
    if (label !== undefined && label.toLowerCase().startsWith(needle)) {
      return index;
    }
  }
  return undefined;
}

export function treeNodeLabel(node: RedoCheckboxTreeNode): string {
  return node.kind === "group" ? node.group.label : node.item.label;
}

export function firstSelectedNodeId(
  nodes: readonly RedoCheckboxTreeNode[],
  selected: ReadonlySet<string>,
): string | undefined {
  for (const node of nodes) {
    if (node.kind === "item" && selected.has(node.item.id)) {
      return node.nodeId;
    }
  }
  for (const node of nodes) {
    if (
      node.kind === "group" &&
      node.items.some((item) => selected.has(item.id))
    ) {
      return node.nodeId;
    }
  }
  return nodes[0]?.nodeId;
}

export function ariaCheckedValue(value: CheckboxValue): boolean | "mixed" {
  if (value === "indeterminate") {
    return "mixed";
  }
  return value;
}
